import React from "react";

export const TermsOfUse: React.FC = () => {
  return (
    <section className="secondary-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 sm:mb-12 lg:mb-12">
          <h1>Terms of Use</h1>
          <p className="edited-date">Effective Date: 18th of August 2026</p>
        </div>
        <div className="mb-8 sm:mb-12 lg:mb-12">
          <p className="l0-section-text">
            These Terms of Use ("Terms") apply to all individuals ("you",
            "user") who access or use the{" "}
            <span className="logo-font">LAB ZER0</span> subscription platform
            (the "Platform"). By creating an account or using the Platform, you
            agree to these Terms. If you do not agree, you must not use the
            Platform.
          </p>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">
            1. Eligibility and Age Restriction
          </h2>
          <div className="l0-section-text">
            <ul className="l0-list">
              <li>
                The Platform is intended for use only by individuals who are 18
                years of age or older.
              </li>
              <li>
                By using the Platform, you confirm that:
                <ul className="l0-list">
                  <li>You are at least 18 years old.</li>
                  <li>
                    You are a current employed member of the organisation
                    (company, institute, or laboratory) that has registered for
                    the Platform, and you are authorised by that organisation to
                    access and use the Platform.
                  </li>
                  <li>
                    You will not share your licence, login credentials, or
                    access with any third party. Your subscription is for your
                    individual use only within your organisation.
                  </li>
                  <li>
                    You are legally permitted to use the Platform in your
                    jurisdiction.
                  </li>
                  <li>
                    You have the full right, power, and authority to agree to
                    and be bound by these Terms and to fully perform all of your
                    obligations under them.
                  </li>
                </ul>
              </li>
              <li>
                If you are under 18, not employed by the registered
                organisation, or otherwise unable to form a binding legal
                contract, you must not access or use the Platform.
              </li>
              <li>
                For the purposes of eligibility, "employed member" means a staff
                member or student who holds a registered domain account issued
                by the subscribing organisation, which is the credential used to
                register for and access the Platform. Contractors, consultants,
                and third-party affiliates are expressly excluded.
              </li>
            </ul>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">2. Purpose of the Platform</h2>
          <div className="l0-section-text">
            <ul className="l0-list">
              <li>
                The Platform is a self-guided sustainability tool designed to
                support laboratory and research groups in assessing, tracking,
                and improving environmental practices.
              </li>
              <li>
                The Platform provides baseline and savings assessments,
                checklists, estimate impact reports, and guidance resources for
                informational purposes only.
              </li>
              <li>
                Estimate impact reports are guides, not scientific certainties.
                They are based on algorithms that capture data using the best of{" "}
                <span className="logo-font">LAB ZER0</span>'s current knowledge.
                Variables such as cost savings, energy reduction, recycling
                inputs, and emissions reduction are calculated using assumptions
                that may change as technology and knowledge advance.
              </li>
              <li>
                The Platform is not a substitute for professional advice,
                institutional approvals, or regulatory compliance.
              </li>
            </ul>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">3. User Responsibilities</h2>
          <div className="l0-section-text">
            <p>When using the Platform, you are responsible for:</p>
            <ul className="l0-list">
              <li>
                Compliance with laws and policies: You must comply with all
                applicable local, state, and national laws, as well as your
                organisation's policies, internal rules, and governance
                procedures.
              </li>
              <li>
                Seeking internal approvals: Before taking any action based on
                outputs from the Platform, you must consult and obtain guidance
                from:
                <ul className="l0-list">
                  <li>Your Occupational Health and Safety (OHS) teams</li>
                  <li>
                    Your Institutional Biosafety Committee (IBC) or equivalent
                    body
                  </li>
                  <li>Your legal or compliance teams</li>
                  <li>
                    Your ICT/IT and Computational Facility Managers (for
                    technology, data, or computational resource impacts)
                  </li>
                  <li>
                    Your lab group leader, principal investigator, or equivalent
                    team manager
                  </li>
                </ul>
              </li>
              <li>
                Accuracy of data: You are solely responsible for the accuracy,
                legality, and appropriateness of any information you enter into
                the Platform.
              </li>
            </ul>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">
            4. Platform Scope and Limitations
          </h2>
          <div className="l0-section-text">
            <ul className="l0-list">
              <li>
                Outputs, insights, baseline and savings assessments, estimate
                impact reports, or checklists provided by the Platform are for
                informational and benchmarking purposes only.
              </li>
              <li>
                <span className="logo-font">LAB ZER0</span> does not guarantee
                the accuracy, completeness, or future validity of any outputs.
              </li>
              <li>
                <span className="logo-font">LAB ZER0</span> does not provide
                legal, safety, compliance, or technical advice.
              </li>
              <li>
                <span className="logo-font">LAB ZER0</span> does not validate or
                verify user-entered data.
              </li>
              <li>
                Decisions made or actions taken based on Platform outputs remain
                your sole responsibility and must be confirmed with your
                internal experts and governance bodies.
              </li>
              <li>
                If you intend to use the Platform's certifications, guides,
                baseline and savings assessments, or estimate impact reports in
                connection with grant submissions, regulatory filings, or
                compliance certifications,{" "}
                <span className="logo-font">LAB ZER0</span> expressly disclaims
                all liability for such use and for any negative outcomes that
                may result.
              </li>
            </ul>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">
            5. Restrictions on Use of the Platform
          </h2>
          <div className="l0-section-text">
            <p>You must not, whether directly or through a third party:</p>
            <ul className="l0-list">
              <li>
                Rent, lease, sell, distribute, offer in a service bureau,
                sublicense, or otherwise make available the Platform or its
                contents to any third party (except as expressly permitted under
                these Terms).
              </li>
              <li>
                Share your access, licence, or login credentials with anyone
                else, including colleagues. Your licence is individual and
                non-transferable.
              </li>
              <li>
                Copy, replicate, decompile, reverse engineer, attempt to derive
                the source code of, modify, or create derivative works of the
                Platform or any part of it.
              </li>
              <li>
                Attempt to bypass, disable, or interfere with any security
                features or access controls, including features designed to
                prevent copying, downloading, or misuse.
              </li>
              <li>
                Use any form of data mining, scraping, harvesting, or automated
                extraction from the Platform or its contents, including for AI,
                machine learning, or data science purposes.
              </li>
              <li>
                Access the Platform for purposes of performance benchmarking.
              </li>
              <li>
                Access the Platform for purposes of building, training, or
                marketing a competitive product or service.
              </li>
              <li>
                Use the Platform to store, upload, or transmit viruses,
                malicious code, or harmful content.
              </li>
              <li>
                Use a VPN or similar tool to circumvent licensing or access
                restrictions.
              </li>
              <li>
                Use the Platform to transmit unsolicited emails, spam, or other
                promotional content.
              </li>
            </ul>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">6. Intellectual Property</h2>
          <div className="l0-section-text">
            <ul className="l0-list">
              <li>
                <span className="logo-font">LAB ZER0</span> reserves the right
                to audit account usage and suspend accounts immediately if login
                sharing, unauthorised access, or misuse of credentials is
                suspected.
              </li>
              <li>
                <span className="logo-font">LAB ZER0</span> further reserves the
                right to seek injunctive relief and damages, in addition to
                termination of access, for any unauthorised use of its
                intellectual property, including but not limited to
                infringement, scraping, reverse engineering, or competitive
                misuse of the Platform.
              </li>
              <li>
                All rights, title, and interest in the Platform and its content
                (including baseline and savings assessments, estimate impact
                reports, checklists, and designs) remain the exclusive property
                of <span className="logo-font">LAB ZER0</span> Pty Ltd.
              </li>
              <li>
                You are granted a limited, non-exclusive licence to use the
                Platform for your organisation's internal sustainability
                purposes.
              </li>
              <li>
                Except as expressly permitted, no rights are granted to you.
              </li>
            </ul>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">7. Data and Privacy</h2>
          <div className="l0-section-text">
            <ul className="l0-list">
              <li>
                All data you or your organisation enter into the Platform ("User
                Data") remains the property of your organisation.
              </li>
              <li>
                <span className="logo-font">LAB ZER0</span> will handle personal
                information in accordance with the Privacy Act 1988 (Cth) and
                the Australian Privacy Principles (APPs).
              </li>
              <li>
                <span className="logo-font">LAB ZER0</span> may use
                de-identified or aggregated data for analytics, benchmarking,
                and platform improvement.
              </li>
              <li>
                For the avoidance of doubt, "de-identified data" means that{" "}
                <span className="logo-font">LAB ZER0</span> will not use your
                organisation's name or address when aggregating results or data
                for benchmarking, marketing, platform improvement, or research
                purposes.
              </li>
              <li>
                <span className="logo-font">LAB ZER0</span> will not disclose
                User Data to third parties except as required by law or to
                trusted service providers under confidentiality obligations.
              </li>
            </ul>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">8. Liability Disclaimer</h2>
          <div className="l0-section-text">
            <ul className="l0-list">
              <li>
                The Platform is provided "as is".{" "}
                <span className="logo-font">LAB ZER0</span> makes no warranties
                about its accuracy, completeness, or fitness for a particular
                purpose.
              </li>
              <li>
                <span className="logo-font">LAB ZER0</span> is not liable for:
                <ul className="l0-list">
                  <li>
                    Errors, omissions, or inaccuracies in data entered by users.
                  </li>
                  <li>
                    Any reliance on outputs, baseline and savings assessments,
                    or estimate impact reports generated by the Platform.
                  </li>
                  <li>
                    Any failure by you to consult with your internal governance
                    teams before taking action.
                  </li>
                  <li>
                    Any loss, damage, or compliance failures resulting from use
                    of the Platform.
                  </li>
                </ul>
              </li>
              <li>
                You and your organisation remain solely responsible for
                validating and implementing any actions suggested by the
                Platform.
              </li>
              <li>
                To the maximum extent permitted by law,{" "}
                <span className="logo-font">LAB ZER0</span>'s total cumulative
                liability for all claims in connection with these Terms is
                capped at the total subscription fees paid by the organisation
                in the twelve (12) months preceding the claim.
              </li>
            </ul>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">9. Termination of Access</h2>
          <div className="l0-section-text">
            <ul className="l0-list">
              <li>
                <span className="logo-font">LAB ZER0</span> may suspend or
                terminate your access if you breach these Terms.
              </li>
              <li>
                Upon termination, you must stop using the Platform immediately.
              </li>
            </ul>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">10. Amendments to the Terms</h2>
          <div className="l0-section-text">
            <ul className="l0-list">
              <li>
                <span className="logo-font">LAB ZER0</span> may amend these
                Terms from time to time.
              </li>
              <li>
                Material changes will be communicated to users via email or
                platform notification.
              </li>
              <li>
                Continued use of the Platform after changes take effect
                constitutes acceptance of the revised Terms.
              </li>
            </ul>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">11. Warranty Disclaimer</h2>
          <div className="l0-section-text">
            <ul className="l0-list">
              <li>
                The Platform is provided on an "as-is" and "as-available" basis.
              </li>
              <li>
                To the maximum extent permitted by law, and subject to any
                non-excludable rights and remedies you may have under applicable
                law, <span className="logo-font">LAB ZER0</span> and its
                licensors and suppliers expressly disclaim all warranties of any
                kind, whether express or implied, including (but not limited to)
                warranties of merchantability, fitness for a particular purpose,
                or non-infringement.
              </li>
              <li>
                <span className="logo-font">LAB ZER0</span> does not warrant
                that:
                <ul className="l0-list">
                  <li>
                    Your use of the Platform will be uninterrupted or
                    error-free.
                  </li>
                  <li>
                    The Platform will review, validate, or preserve the accuracy
                    of any data you enter.
                  </li>
                  <li>
                    The Platform will store or maintain your data without loss.
                  </li>
                </ul>
              </li>
              <li>
                You understand that use of the Platform necessarily involves the
                transmission of data over networks that{" "}
                <span className="logo-font">LAB ZER0</span> does not own,
                operate, or control. <span className="logo-font">LAB ZER0</span>{" "}
                is not responsible for any of your data that is lost, altered,
                intercepted, or stored across such networks.
              </li>
              <li>
                <span className="logo-font">LAB ZER0</span> will not be liable
                for delays, interruptions, service failures, or other problems
                inherent in the use of the internet, electronic communications,
                or systems outside <span className="logo-font">LAB ZER0</span>'s
                reasonable control.
              </li>
              <li>
                <span className="logo-font">LAB ZER0</span> is not responsible
                for losses or damages arising from cyberattacks, third-party
                hosting outages, or force majeure events, as these fall outside
                of <span className="logo-font">LAB ZER0</span>'s direct control.
              </li>
            </ul>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">12. Governing Law</h2>
          <div className="l0-section-text">
            <p>
              These Terms are governed exclusively by the laws of Victoria,
              Australia, regardless of conflict-of-law principles, and the
              United Nations Convention on Contracts for the International Sale
              of Goods does not apply. If you access or use the Platform outside
              of Australia and in a laboratory not subject to Australian
              regulations, you do so entirely at your own discretion and risk.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

TermsOfUse.displayName = "TermsOfUse";
